import _XCircleFilledIcon from "@adsk/alloy-react-icon/es/lib/XCircleFilledIcon";
import _MagnifyingGlassIcon from "@adsk/alloy-react-icon/es/lib/MagnifyingGlassIcon";
const _excluded = ["disabled", "loading", "type", "defaultValue", "inlineText", "onClear", "onSearch", "value", "onChange"],
  _excluded2 = ["size"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styledC from 'styled-components';
import { IconButton } from '@adsk/alloy-react-button';
import { useControlled, useForwardedRef } from '@adsk/alloy-react-helpers';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import TextInput from '@adsk/alloy-react-text-input';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import useKeyDown from './hooks/useKeyDown';
const StyledTextInput = styledC(TextInput).attrs({
  role: 'searchbox',
  ['data-testid']: 'SearchField__input'
}).withConfig({
  displayName: "SearchField__StyledTextInput",
  componentId: "sc-k7hn6i-0"
})(["& input[type=\"search\"]{padding-bottom:0px;", ";}& input[type=\"search\"]::-webkit-search-decoration,input[type=\"search\"]::-webkit-search-cancel-button,input[type=\"search\"]::-webkit-search-results-button,input[type=\"search\"]::-webkit-search-results-decoration{display:none;}"], theme.typography.ellipsis);
const StyledMagnifyingGlassButton = styledC(IconButton).attrs({
  renderIcon: _MagnifyingGlassIcon.renderIcon
}).withConfig({
  displayName: "SearchField__StyledMagnifyingGlassButton",
  componentId: "sc-k7hn6i-1"
})(["color:", ";", ""], theme.tokens.colors.icon.tertiary.value, props => !props.disabled && {
  pointerEvents: 'auto',
  cursor: 'pointer'
});
const StyledDiv = styledC.div.withConfig({
  displayName: "SearchField__StyledDiv",
  componentId: "sc-k7hn6i-2"
})(["height:36px;display:flex;flex-flow:row nowrap;justify-content:flex-end;align-items:center;gap:", "px;"], theme.spacing.XS);
const StyledSpan = styledC.span.withConfig({
  displayName: "SearchField__StyledSpan",
  componentId: "sc-k7hn6i-3"
})(["", ";color:", ";pointer-events:none;"], theme.typography.bodySmall, theme.tokens.colors.text.primary.value);

/**
 * A SearchField component with a magnifying glass and a clear input text button
 */
const SearchField = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var _defaultValue$toStrin;
  let {
      disabled = false,
      loading = false,
      type = 'search',
      defaultValue,
      inlineText = '',
      onClear,
      onSearch,
      value: valueProp,
      onChange: onChangeProp
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const inputRef = useForwardedRef(ref);
  const [value, onChange] = useControlled((_defaultValue$toStrin = defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.toString()) !== null && _defaultValue$toStrin !== void 0 ? _defaultValue$toStrin : '', valueProp, onChangeProp);
  const clearInput = useCallback(() => {
    onChange === null || onChange === void 0 ? void 0 : onChange('');
    onClear === null || onClear === void 0 ? void 0 : onClear();
  }, [onChange, onClear]);
  useKeyDown(inputRef.current, 'Escape', clearInput);
  useKeyDown(inputRef.current, 'Enter', () => onSearch === null || onSearch === void 0 ? void 0 : onSearch());
  useKeyDown(inputRef.current, 'NumpadEnter', () => onSearch === null || onSearch === void 0 ? void 0 : onSearch());
  const renderLeftElement = useCallback(magnifyingGlassProps => /*#__PURE__*/React.createElement(StyledMagnifyingGlassButton, _extends({}, magnifyingGlassProps, {
    disabled: disabled,
    onClick: () => {
      var _inputRef$current;
      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
      onSearch === null || onSearch === void 0 ? void 0 : onSearch();
    }
  })), [disabled, inputRef, onSearch]);
  const renderRightElement = useCallback(_ref2 => {
    let {
        size: iconSize
      } = _ref2,
      rightContainerProps = _objectWithoutProperties(_ref2, _excluded2);
    return /*#__PURE__*/React.createElement(StyledDiv, _extends({}, rightContainerProps, {
      style: _objectSpread(_objectSpread({}, rightContainerProps.style), {}, {
        pointerEvents: 'auto'
      })
    }), inlineText && /*#__PURE__*/React.createElement(StyledSpan, null, inlineText), loading && /*#__PURE__*/React.createElement(ProgressRing, {
      "data-testid": "progress-ring",
      size: ProgressRing.SIZES.XSMALL
    }), value && !disabled && /*#__PURE__*/React.createElement(IconButton, {
      "data-testid": "SearchField__ClearButton",
      tabIndex: -1,
      size: iconSize,
      renderIcon: _XCircleFilledIcon.renderIcon,
      onClick: e => {
        e.preventDefault();
        clearInput();
      }
    }));
  }, [clearInput, disabled, inlineText, loading, value]);
  return /*#__PURE__*/React.createElement(StyledTextInput, _extends({
    value: value,
    type: type
  }, props, {
    ref: inputRef,
    disabled: disabled,
    onChange: e => onChange(e.target.value),
    renderLeftElement: renderLeftElement,
    renderRightElement: value || inlineText || loading ? renderRightElement : undefined
  }));
});
SearchField.displayName = 'SearchField';
SearchField.propTypes = {
  /* Styles applied to the input element */
  style: stylePropType,
  /* Class applied to the input element */
  className: PropTypes.string,
  /* Toggle disabled state */
  disabled: PropTypes.bool,
  /* Toggle loading spinner */
  loading: PropTypes.bool,
  /* initial default text value */
  defaultValue: PropTypes.string,
  /* controlled input value */
  value: PropTypes.string,
  /* Child input's onChange handler */
  onChange: PropTypes.func,
  /* Any text to display at the right of the input,
   * intended to show the number of results returned from the search
   **/
  inlineText: PropTypes.string
};
export default SearchField;
//# sourceMappingURL=SearchField.js.map